// packages import
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { FC, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
// component import
import { ContentHead } from '../common/ContentHead'
import { EmptyData } from '../common/EmptyData'
import { CasesSkeleton } from '../skeletons/CasesSkeleton'
import ClientDropdown from '../common/ClientDropdown'
// others
import { CaseStatus, useSearchMattersLazyQuery, ExternalUser, Maybe } from '../../../generated'
import {
  CASE_SEARCH_INFO_TEXT,
  CASE_SEARCH_PLACEHOLDER_WITH_CLIENT,
  DCPP,
  GRAPHQL_QUERY_POLICY,
  MIN_SEARCH_LENGTH,
  MIN_SEARCH_LENGTH_TEXT,
  NO_BILLING_TYPE_LABEL,
  NO_BILLING_TYPE_TOOLTIP,
  NO_CASES_FOUND,
} from '../../constants'
import { flexCenter } from '../../theme/styleConstants'
import { CaseStickyTable } from '../common/CaseStickyTable'
import { PaginationWithJump } from '../common/PaginationWithJump'
import { AuthContext } from '../../context'
import { CrossIcon } from '../../assets/images'

const SEARCH_MATTER_TABLE_HEADERS = [
  'Matter #',
  'Adv Case #',
  'Title',
  'Status',
  'Open Date',
  'Completed Date',
  'Close Date',
  'Created At',
  'Updated At',
  'Billing Paused Until',
]

// Format matter status with extra context: an Active matter whose work is done shows as
// "Active" with a "(Work Completed)" suffix on the next line; a Completed matter shows
// as "Completed" with a "(Closed)" suffix on the next line.
const formatMatterStatus = (
  status: string | null | undefined,
  completedDate: string | number | null | undefined,
): { primary: string; suffix?: string } => {
  if (!status) return { primary: 'N/A' }
  const normalized = String(status).toUpperCase()
  if (normalized === CaseStatus.Active) {
    return completedDate ? { primary: CaseStatus.Active, suffix: '(WORK COMPLETED)' } : { primary: CaseStatus.Active }
  }
  if (normalized === CaseStatus.Completed) return { primary: CaseStatus.Completed, suffix: '(CLOSED)' }
  return { primary: status }
}

// Helper function to format date in US format (MM/DD/YYYY)
// Handles both Unix timestamps (milliseconds as string/number) and ISO date strings
const formatDateUS = (dateValue: string | number | null | undefined): string => {
  if (!dateValue) return 'N/A'
  try {
    let date: Date
    
    // Check if it's a Unix timestamp (numeric string or number)
    if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
      // It's a Unix timestamp string (milliseconds)
      date = new Date(parseInt(dateValue, 10))
    } else if (typeof dateValue === 'number') {
      // It's a Unix timestamp number (milliseconds)
      date = new Date(dateValue)
    } else {
      // It's an ISO date string
      date = new Date(dateValue)
    }
    
    if (isNaN(date.getTime())) return 'N/A'
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()
    return `${month}/${day}/${year}`
  } catch {
    return 'N/A'
  }
}

export const SearchMatterContent: FC = (): JSX.Element => {
  const { isDcpp, isAdmin, isSuperAdmin, isAccountant } = useContext(AuthContext)

  const [searchValue, setSearchValue] = useState('')
  const [matterId, setMatterId] = useState('')
  const [clientId, setClientId] = useState('')
  const [billId, setBillId] = useState('')
  const [matterTitle, setMatterTitle] = useState('')
  const [status, setStatus] = useState<CaseStatus | 'ALL'>('ALL')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedClient, setSelectedClient] = useState<Maybe<ExternalUser> | null>(null)
  const [billingPaused, setBillingPaused] = useState(false)
  const [noBillingDetails, setNoBillingDetails] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const navigate = useNavigate()

  // Free-text search requires at least MIN_SEARCH_LENGTH characters (matches the Active matters search bar)
  const trimmedSearch = searchValue.trim()
  const isSearchTooShort = trimmedSearch.length > 0 && trimmedSearch.length < MIN_SEARCH_LENGTH
  // Only send the free-text term to the API once it is long enough to be meaningful
  const searchParam = trimmedSearch.length >= MIN_SEARCH_LENGTH ? trimmedSearch : undefined

  const [searchMatters, { loading, data }] = useSearchMattersLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError() {
      // Handle error silently or show notification
    },
  })

  // Fetch all cases on mount (default behavior)
  useEffect(() => {
    searchMatters({
      variables: {
        searchMattersInput: {
          status: status === 'ALL' ? undefined : status,
          paginationOptions: {
            page: 1,
            limit: 50,
          },
        },
      },
    })
    setHasSearched(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleStatusChange = (event: SelectChangeEvent) => {
    const newStatus = event.target.value as CaseStatus | 'ALL'
    setStatus(newStatus)
    setCurrentPage(1)
    setHasSearched(true)
    searchMatters({
      variables: {
        searchMattersInput: {
          search: searchParam,
          matterId: matterId || undefined,
          clientId: clientId || undefined,
          billId: billId || undefined,
          title: matterTitle || undefined,
          status: newStatus === 'ALL' ? undefined : newStatus,
          billingPaused: billingPaused || undefined,
          noBillingDetails: noBillingDetails || undefined,
          paginationOptions: {
            page: 1,
            limit: 50,
          },
        },
      },
    })
  }

  const handleSearch = () => {
    setCurrentPage(1)
    setHasSearched(true)
    searchMatters({
      variables: {
        searchMattersInput: {
          search: searchParam,
          matterId: matterId || undefined,
          clientId: clientId || undefined,
          billId: billId || undefined,
          title: matterTitle || undefined,
          status: status === 'ALL' ? undefined : status,
          billingPaused: billingPaused || undefined,
          noBillingDetails: noBillingDetails || undefined,
          paginationOptions: {
            page: 1,
            limit: 50,
          },
        },
      },
    })
  }

  const handleClientChange = (client: Maybe<ExternalUser>) => {
    setSelectedClient(client)
    // Use clientNumber from selected client for the API call
    setClientId(client?.clientNumber || '')
  }

  const handleClear = () => {
    setSearchValue('')
    setMatterId('')
    setClientId('')
    setBillId('')
    setMatterTitle('')
    setStatus(CaseStatus.Active)
    setCurrentPage(1)
    setSelectedClient(null)
    setBillingPaused(false)
    setNoBillingDetails(false)
    setHasSearched(false)
    // Clear data on frontend only - no API call
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    setHasSearched(true)
    searchMatters({
      variables: {
        searchMattersInput: {
          search: searchParam,
          matterId: matterId || undefined,
          clientId: clientId || undefined,
          billId: billId || undefined,
          title: matterTitle || undefined,
          status: status === 'ALL' ? undefined : status,
          billingPaused: billingPaused || undefined,
          noBillingDetails: noBillingDetails || undefined,
          paginationOptions: {
            page,
            limit: 50,
          },
        },
      },
    })
  }

  const showDcppCases = isDcpp || isAdmin || isSuperAdmin || isAccountant
  // Only show data if a search has been performed
  const searchResults = hasSearched ? data?.searchMatters : null
  const cases = hasSearched
    ? showDcppCases
      ? searchResults?.cases || []
      : (searchResults?.cases || [])?.filter(litigation => {
          const { caseType } = litigation || {}
          const { name } = caseType || {}

          return name !== DCPP
        })
    : []
  const pagination = hasSearched ? searchResults?.pagination : null
  const totalPages = pagination?.totalPages ?? 0
  const isEmpty = cases.length === 0 && !loading

  const handleCaseClick = (caseId: string) => {
    navigate(`/matters/matter-detail/${caseId}`)
  }

  return (
    <>
      <ContentHead title="Search Matters" buttonText="" link="" />

      {/* Free-text search in its own box: broad search across matter #, title, adv case # and client name/number */}
      <Card sx={{ marginTop: 2 }}>
        <CardContent>
          <Box maxWidth={380} width="100%">
            <Box display="flex" gap="4px" alignItems="flex-start">
              <TextField
                placeholder={CASE_SEARCH_PLACEHOLDER_WITH_CLIENT}
                fullWidth
                size="small"
                value={searchValue}
                error={isSearchTooShort}
                helperText={isSearchTooShort ? MIN_SEARCH_LENGTH_TEXT : ''}
                FormHelperTextProps={{ sx: { ml: 0 } }}
                onChange={(e) => setSearchValue(e.target.value.slice(0, 50))}
                inputProps={{ maxLength: 50 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch()
                }}
                InputProps={{
                  endAdornment: searchValue ? (
                    <InputAdornment position="end">
                      <IconButton sx={{ p: 0, svg: { maxWidth: '10px' } }} onClick={() => setSearchValue('')}>
                        <CrossIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />
              <Button
                color="primary"
                variant="contained"
                size="small"
                onClick={handleSearch}
                disabled={loading || isSearchTooShort}
              >
                Search
              </Button>
            </Box>
            <Typography variant="caption" color="wardGray" sx={{ display: 'block', mt: 0.5, ml: 0.5 }}>
              {CASE_SEARCH_INFO_TEXT}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ marginTop: 2, marginBottom: 2 }}>
        <CardContent>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* First Row: Matter Title, Matter ID, Status */}
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
              <ClientDropdown
                selectedClientId={selectedClient?.id}
                onChange={handleClientChange}
                label="Client"
                placeholder="Search by client name or ID"
                size="small"
                sx={{ minWidth: 300 }}
              />
              <TextField
                label="Matter ID"
                size="small"
                value={matterId}
                onChange={(e) => setMatterId(e.target.value)}
                placeholder="Enter matter number"
                sx={{ minWidth: 200 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch()
                  }
                }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="status-filter-label">Matter Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status-filter"
                  value={status}
                  label="Matter Status"
                  onChange={handleStatusChange}
                >
                  <MenuItem value="ALL">All</MenuItem>
                  <MenuItem value={CaseStatus.Active}>Active</MenuItem>
                  <MenuItem value={CaseStatus.Archived}>Archived</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {/* Second Row: Client, Client/Co-signer ID, Bill ID, Search, Clear */}
            <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
              <TextField
                label="Matter Title"
                size="small"
                value={matterTitle}
                onChange={(e) => setMatterTitle(e.target.value)}
                placeholder="Enter matter title"
                sx={{ minWidth: 200 }}
                onKeyPress={(e) => {
                if (e.key === 'Enter') {
                    handleSearch()
                }
              }}
              />
              <TextField
                label="Client/Co-signer ID"
                size="small"
                value={clientId}
                onChange={(e) => {
                  setClientId(e.target.value)
                  // Clear selected client if user manually types in the field
                  if (e.target.value !== selectedClient?.clientNumber) {
                    setSelectedClient(null)
                  }
                }}
                placeholder="Enter client ID"
                sx={{ minWidth: 200 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch()
                  }
                }}
              />
              <TextField
                label="Bill ID"
                size="small"
                value={billId}
                onChange={(e) => setBillId(e.target.value)}
                placeholder="Enter bill ID"
                sx={{ minWidth: 200 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch()
                  }
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={billingPaused}
                    onChange={(e) => setBillingPaused(e.target.checked)}
                    size="small"
                  />
                }
                label="Billing Paused"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noBillingDetails}
                    onChange={(e) => setNoBillingDetails(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {NO_BILLING_TYPE_LABEL}
                    <Tooltip title={NO_BILLING_TYPE_TOOLTIP} placement="top" arrow>
                      <InfoOutlinedIcon sx={{ fontSize: 15, color: 'text.secondary', cursor: 'pointer' }} />
                    </Tooltip>
                  </Box>
                }
              />
              <Button variant="contained" color="primary" onClick={handleSearch} disabled={loading}>
                Search
              </Button>
              <Button variant="outlined" color="primary" onClick={handleClear} disabled={loading}>
                Clear
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {isEmpty && !loading && (
        <Box sx={{ ...flexCenter, height: 'calc(100vh - 417px)' }}>
          <EmptyData title={NO_CASES_FOUND} />
        </Box>
      )}

      {(cases.length > 0 || loading) && (
        <CaseStickyTable
          pagination={
            totalPages > 1 ? (
              <PaginationWithJump
                disabled={loading}
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
              />
            ) : null
          }
          loading={loading}
          height="400px"
        >
          <TableHead>
            <TableRow>
              {SEARCH_MATTER_TABLE_HEADERS.map((item, index) => (
                <TableCell component="th" key={`${item}-${index}`} sx={{ minWidth: '150px' }}>
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {loading ? (
            <CasesSkeleton noOfRows={6} />
          ) : (
            <TableBody>
              {cases.map((caseItem) => {
                const matterNumber = caseItem?.isConsultMatter
                  ? caseItem.consultMatterNumber
                  : caseItem?.caseNumber

                return (
                  <TableRow key={caseItem?.id} hover>
                    <TableCell>
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <Box
                          component="span"
                          onClick={() => caseItem?.id && handleCaseClick(caseItem.id)}
                          sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': {
                              textDecoration: 'none',
                            },
                          }}
                        >
                          {matterNumber || 'N/A'}
                        </Box>
                        {caseItem?.id && (
                          <Box
                            component="span"
                            onClick={() => navigate(`/matters/matter-detail/${caseItem.id}?active-tab=2`)}
                            sx={{
                              color: 'primary.main',
                              cursor: 'pointer',
                              textDecoration: 'underline',
                              fontSize: '0.875rem',
                              '&:hover': {
                                textDecoration: 'none',
                              },
                            }}
                          >
                            Financials
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{caseItem?.advCaseNumber || 'N/A'}</TableCell>
                    <TableCell>{caseItem?.title || 'N/A'}</TableCell>
                    <TableCell>
                      {(() => {
                        const { primary, suffix } = formatMatterStatus(caseItem?.status, caseItem?.completedDate)
                        return (
                          <Box display="flex" flexDirection="column" lineHeight={1.2}>
                            <Box component="span">{primary}</Box>
                            {suffix && (
                              <Box component="span" sx={{ fontSize: '1em', color: 'text.secondary', mt: 1 }}>
                                {suffix}
                              </Box>
                            )}
                          </Box>
                        )
                      })()}
                    </TableCell>
                    <TableCell>{formatDateUS(caseItem?.openDate)}</TableCell>
                    <TableCell>{formatDateUS(caseItem?.completedDate)}</TableCell>
                    <TableCell>{formatDateUS(caseItem?.closeDate)}</TableCell>
                    <TableCell>{formatDateUS(caseItem?.createdAt)}</TableCell>
                    <TableCell>{formatDateUS(caseItem?.updatedAt)}</TableCell>
                    <TableCell>{formatDateUS(caseItem?.billing?.pauseBillingUntil)}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          )}
        </CaseStickyTable>
      )}
    </>
  )
}
